import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import {
  ApiSimplePropertyRuleset,
  RuleSetDialogAction,
  RuleSetDialogMode,
} from '@app/features/property-rulesets/property-ruleset.interface';
import { PropertyRulesetService } from '@app/features/property-rulesets/property-ruleset.service';
import { InternalCsService } from '@app/shared/services/internal-cs/internal-cs.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { SharedModule } from '@app/shared/shared.module';
import { UiModule } from '@app/ui';
import { CsDebugItemComponent } from '@app/ui/cs-debug-item/cs-debug-item.component';
import { FormLabelComponent } from '@app/ui/form/form-label/form-label.component';
import { NestedFullScreenModalComponent } from '@app/ui/nested-full-screen-modal/nested-full-screen-modal.component';
import { LoadingSpinnerComponent } from '@ui';

enum DELETE_LOADING_STATES {
  LOADING = 'loading',
  DELETED = 'deleted',
}

@Component({
  selector: 'sbnb-ruleset-search',
  templateUrl: './ruleset-search.component.html',
  styles: `
    .delete-cta {
      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }
    }
  `,
  standalone: true,
  imports: [
    CommonModule,
    UiModule,
    FormLabelComponent,
    NestedFullScreenModalComponent,
    SharedModule,
    LoadingSpinnerComponent,
    CsDebugItemComponent,
  ],
})
export class RulesetSearchComponent {
  private dialogRef = inject(MatLegacyDialogRef<RulesetSearchComponent>);
  private propertyRulesetService = inject(PropertyRulesetService);
  private toast = inject(ToastNotificationsService);

  dialogData: { mode: RuleSetDialogMode } = inject(MAT_DIALOG_DATA);
  deleteLoaders: Record<number, DELETE_LOADING_STATES> = {};
  DELETE_LOADING_STATES = DELETE_LOADING_STATES;
  RuleSetDialogMode = RuleSetDialogMode;

  isCS: boolean;
  debugMode$ = this.csService.debugMode;

  rulesets = signal<ApiSimplePropertyRuleset[] | null>(null);
  filteredRulesets = computed(() =>
    this.searchCriteria()
      ? this.rulesets()?.filter((ruleset) => ruleset.title?.toLowerCase().includes(this.searchCriteria().toLowerCase()))
      : this.rulesets()
  );

  loading = signal(true);
  error = signal<string | null>(null);
  searchCriteria = signal<string>('');

  constructor(private csService: InternalCsService) {
    this.isCS = this.csService.isCS();
    this.loadRulesets();
  }

  loadRulesets() {
    this.propertyRulesetService.getCalendarRulesets().subscribe({
      next: (rulesets) => this.rulesets.set(rulesets),
      error: (error) => this.error.set(error.message || 'Unable to load rule-sets'),
      complete: () => this.loading.set(false),
    });
  }

  selectRuleset(ruleset: ApiSimplePropertyRuleset) {
    this.dialogRef.close(ruleset);
  }

  close() {
    this.dialogRef.close();
  }

  handleRulesetDialogResult(rulesetId: number | null = null, mode: RuleSetDialogMode) {
    const dialogRef = this.propertyRulesetService.openAddEditRulesetDialog({ rulesetId, mode });

    dialogRef.beforeClosed().subscribe((result: { action: RuleSetDialogAction; data: { uuid: string } | null }) => {
      if (
        result.action === RuleSetDialogAction.CREATE ||
        result.action === RuleSetDialogAction.UPDATE ||
        result.action === RuleSetDialogAction.DELETE
      ) {
        this.loadRulesets();
        if (this.dialogData.mode === RuleSetDialogMode.APPLY && result.action === RuleSetDialogAction.CREATE) {
          this.selectRuleset(result.data as unknown as ApiSimplePropertyRuleset);
        }
      }
    });
  }

  createNewRuleset(rulesetId: number | null = null) {
    this.handleRulesetDialogResult(rulesetId, this.dialogData.mode);
  }

  editRuleset(rulesetId: number | null = null) {
    this.handleRulesetDialogResult(rulesetId, RuleSetDialogMode.MANAGE);
  }

  deleteRuleset(ruleset: ApiSimplePropertyRuleset) {
    this.deleteLoaders[ruleset.id] = DELETE_LOADING_STATES.LOADING;
    this.propertyRulesetService.deleteCalendarRuleset(ruleset.id.toString()).subscribe({
      next: () => {
        this.toast.success('Rule-set deleted.');
        this.loadRulesets();
        this.deleteLoaders[ruleset.id] = DELETE_LOADING_STATES.DELETED;
        this.propertyRulesetService.rulesetsUpdated$.next();
      },
      error: () => {
        this.toast.error(`There was a problem deleting rule-set ${ruleset.title}`);
        delete this.deleteLoaders[ruleset.id];
      },
    });
  }
}
